import { inject, Injectable } from '@angular/core';
import { CollaborationsDataService, Patient } from '@ffd/api-client';
import { SelectedParticipant } from '@libs/collaboration';
import { ToastService } from '@libs/shared-services';

import { DataState, DISABLE_GLOBAL_ERROR_TOASTS, FfdComponentStore, routes } from '@libs/utils';
import { tapResponse, concatLatestFrom } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, switchMap } from 'rxjs';
import { HttpContext } from '@angular/common/http';
import { ParticipantListItemType, ParticipantsList } from '../search-participants';
import {
  CollaborationEditorDialogService,
  MINIMUM_SEARCH_LENGTH,
} from '../collaboration-editor-dialog/collaboration-editor-dialog.service';

interface LinkPatientDialogState {
  lastPatientSearchTerm: string | null;
  selectedPatientParticipant: SelectedParticipant | null;
}

export interface LinkPatientDialogVM {
  allPatients: Patient[];
  patientOptions?: ParticipantsList;
  patientsLoading: boolean;
  patientParticipant: SelectedParticipant | null;
  isShowcase: boolean;
}

const OPTIONS_LIMIT = 20;

@Injectable()
export class LinkPatientDialogService extends FfdComponentStore<LinkPatientDialogState, LinkPatientDialogVM> {
  private readonly collaborationsDataService = inject(CollaborationsDataService);
  private readonly translateService = inject(TranslateService);
  private readonly toastMessageService = inject(ToastService);
  private readonly collaborationEditorDialogService = inject(CollaborationEditorDialogService);

  constructor() {
    super({
      selectedPatientParticipant: null,
      lastPatientSearchTerm: '',
    });
  }

  protected setLastPatientSearchTerm = this.updater((state, lastSearchTerm: string | null): LinkPatientDialogState => {
    return {
      ...state,
      lastPatientSearchTerm: lastSearchTerm,
    };
  });

  public readonly setPatientParticipant = this.updater((state, participant: SelectedParticipant): LinkPatientDialogState => {
    return {
      ...state,
      selectedPatientParticipant: participant,
    };
  });

  public readonly removePatientParticipant = this.updater((state): LinkPatientDialogState => {
    return {
      ...state,
      selectedPatientParticipant: null,
    };
  });

  public override init(): void {}

  public updatePatientSearchTerm(searchTerm: string | null): void {
    if (searchTerm !== this.state().lastPatientSearchTerm) {
      this.collaborationEditorDialogService.setPatientsLoading();
      this.collaborationEditorDialogService.patchState({
        patients: { data: [], state: DataState.Loading },
      });
    }

    this.setLastPatientSearchTerm(searchTerm);
    if (searchTerm && searchTerm.length >= MINIMUM_SEARCH_LENGTH) {
      this.searchPatients({ searchTerm });
    }
  }

  public readonly searchPatients = this.effect<{ searchTerm?: string }>(($) =>
    $.pipe(
      concatLatestFrom(() => [
        this.collaborationEditorDialogService.patients$,
        this.collaborationEditorDialogService.patientsTotalCount$,
        this.select(() => this.collaborationEditorDialogService.state().siteId),
        this.select((state) => state.lastPatientSearchTerm),
      ]),
      distinctUntilChanged((a, b) => a[0].searchTerm === b[0].searchTerm && a[1].data?.length === b[1].data?.length),
      filter(([_, patients, totalCount]) => !totalCount || totalCount > (patients.data?.length ?? 0)),
      switchMap(([{ searchTerm }, patients, __, siteId, lastSearchTerm]) => {
        this.collaborationEditorDialogService.setPatientsLoading();
        const offset = searchTerm ? 0 : patients.data?.length ?? 0;
        const nextSearchTerm = searchTerm ?? lastSearchTerm ?? '';
        this.setLastPatientSearchTerm(nextSearchTerm);
        return this.collaborationsDataService
          .searchParticipantsPatients(OPTIONS_LIMIT, offset, nextSearchTerm, siteId, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              ({ list, total }) => {
                if (searchTerm) {
                  this.collaborationEditorDialogService.setPatients(list);
                } else {
                  this.collaborationEditorDialogService.appendPatients(list);
                }
                this.collaborationEditorDialogService.setTotalPatientsCount(total);
              },
              () => {
                this.toastMessageService.error(
                  this.translateService.instant('collab_new_collab_modal_collaboration_toast_search_patients_error'),
                );
              },
            ),
          );
      }),
    ),
  );

  protected override mapStateToViewModel(state: LinkPatientDialogState): LinkPatientDialogVM {
    const patients = this.collaborationEditorDialogService.state().patients;
    return {
      allPatients: patients.data ?? [],
      patientOptions: this.mapPatientsToParticipantList(patients.data, state.selectedPatientParticipant),
      patientsLoading: patients.state === DataState.Loading,
      patientParticipant: state.selectedPatientParticipant,
      isShowcase: this.collaborationEditorDialogService.state().siteId === routes.showcaseSiteId,
    };
  }

  protected readonly mapPatientsToParticipantList = (
    patients?: Patient[],
    excludedParticipant?: SelectedParticipant | null,
  ): ParticipantsList | undefined => {
    if (!patients) {
      return;
    }

    const filteredPatients = patients?.filter((participant) => {
      return excludedParticipant?.id !== participant.id;
    });

    return filteredPatients.map((patient) => ({
      ...patient,
      type: ParticipantListItemType.Participant,
    }));
  };
}
